import React, { useEffect, useMemo, useState } from 'react';
import { Button, Box, Typography } from '@mui/material';
import { AppSkinProps } from '../../../../types/decisions';
import CriteriaSetting from '../CriteriaSetting';
import './CriteriaSettingsV2.scss';

interface CriteriaSettingsProps {
    criteriaFields: AppSkinProps['criteriaData']['fields'];
    fieldInitialValues: AppSkinProps['fieldInitialValues'];
    onTradeoffValueChange: AppSkinProps['onTradeoffValueChange'];
    onSliderChange: AppSkinProps['onSliderChange'];
    handleClose?: () => void;
    openCriteriaSettings?: (id: string) => void;
    disabled?: boolean;
    renderCustomSettings?: boolean;
}

const CriteriaSettingsV2 = ({
    criteriaFields,
    fieldInitialValues,
    onTradeoffValueChange,
    onSliderChange,
    handleClose,
    openCriteriaSettings,
    disabled = false,
    renderCustomSettings = false,
}: CriteriaSettingsProps) => {
    const visibleFields = useMemo(
        () => criteriaFields.filter((field) => !field.hide_field),
        [ criteriaFields ],
    );

    const [ currentIndex, setCurrentIndex ] = useState(0);
    const [ direction, setDirection ] = useState<'next' | 'prev'>('next');
    const [ isAnimating, setIsAnimating ] = useState(false);

    useEffect(() => {
        if (currentIndex > visibleFields.length - 1) {
            setCurrentIndex(0);
        }
    }, [ visibleFields, currentIndex ]);

    useEffect(() => {
        if (!isAnimating) {
            return;
        }

        const timer = setTimeout(() => {
            setIsAnimating(false);
        }, 280);

        return () => clearTimeout(timer);
    }, [ isAnimating ]);

    if (!visibleFields.length) {
        return null;
    }

    const currentField = visibleFields[currentIndex];
    const isFirst = currentIndex === 0;
    const isLast = currentIndex === visibleFields.length - 1;

    const handleNext = () => {
        if (isLast || isAnimating) {
            return;
        }
        setDirection('next');
        setIsAnimating(true);
        setCurrentIndex((prev) => prev + 1);
    };

    const handleBack = () => {
        if (isFirst || isAnimating) {
            return;
        }
        setDirection('prev');
        setIsAnimating(true);
        setCurrentIndex((prev) => prev - 1);
    };

    return (
        <div className="criteria-settings-container-v2">
            <div className="criteria-settings-card-wrapper">

                <Box className="criteria-settings-header">
                    <Typography className="criteria-settings-progress">
                        {currentIndex + 1} / {visibleFields.length}
                    </Typography>

                    <div className="criteria-settings-dots">
                        {visibleFields.map((field, index) => (
                            <span
                                key={field.id}
                                className={`criteria-settings-dot ${
                                    index === currentIndex ? 'active' : ''
                                }`}
                            />
                        ))}
                    </div>
                </Box>

                <div
                    className={`criteria-settings-card ${
                        isAnimating ? `animate-${direction}` : ''
                    }`}
                >
                    <CriteriaSetting
                        key={currentField.id}
                        field={currentField}
                        fieldInitialValues={fieldInitialValues}
                        onTradeoffValueChange={onTradeoffValueChange}
                        onSliderChange={onSliderChange}
                        handleModalClose={handleClose}
                        openCriteriaSettings={openCriteriaSettings}
                        disabled={disabled}
                        renderCustomSettings={renderCustomSettings}
                    />
                </div>

                <Box className="criteria-settings-actions">
                    <Button
                        variant="outlined"
                        onClick={handleBack}
                        disabled={disabled || isFirst || isAnimating}
                    >
                        Back
                    </Button>

                    {isLast ? (
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            disabled
                        >
                            Done
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={disabled || isAnimating}
                        >
                            Next
                        </Button>
                    )}
                </Box>

            </div>
        </div>
    );
};

export default CriteriaSettingsV2;
